import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '@/store';
import { fetchFilteredProviders, setProviderFilter } from '@/features/provider/providerSlice';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Search, Filter, X, Edit, Trash2, ExternalLink, Mail } from 'lucide-react';
import { tunisianGovernorates, governorateCities } from '@/data/tunisian-governorates';
import { useToast } from '@/components/ui/use-toast';
import ProviderLogo from "@/components/common/ProviderLogo";

const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  const pages = Array.from({ length: totalPages }, (_, i) => i + 1);

  return (
    <div className="flex justify-center mt-6 space-x-2">
      <Button variant="outline" size="sm" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}>
        Previous
      </Button>
      {pages.map(page => (
        <Button
          key={page}
          variant={currentPage === page ? "default" : "outline"}
          size="sm"
          onClick={() => onPageChange(page)}
        >
          {page}
        </Button>
      ))}
      <Button variant="outline" size="sm" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages}>
        Next
      </Button>
    </div>
  );
};

const ProvidersList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { providers, paginationMeta, loading, error, filter } = useSelector((state: RootState) => state.provider);

  // Local state for filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedOwnership, setSelectedOwnership] = useState('all');
  const [selectedCountry, setSelectedCountry] = useState('Tunisia');
  const [selectedCity, setSelectedCity] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedState, setSelectedState] = useState('all');

  // Debounce search input
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      applyFilters();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Fetch providers on mount
  useEffect(() => {
    dispatch(fetchFilteredProviders(filter)as any);
  }, [dispatch, filter]);

  // Update the formatLogoUrl function to properly handle logo URLs
  const formatLogoUrl = (url) => {
    if (!url) return '';
    
    // If it's already a full URL, return it as is
    if (url.startsWith('http')) {
      return url;
    }
    
    // Otherwise, just return the filename which our ProviderLogo component can handle
    return url;
  };

  const formatProviderType = (type) => {
    return type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const applyFilters = () => {
    const newFilter = {
      search: searchTerm || null,
      type: selectedType === 'all' ? null : selectedType,
      ownership: selectedOwnership === 'all' ? null : selectedOwnership,
      country: selectedCountry || null,
      city: selectedCity === 'all' ? null : selectedCity,
      state: selectedState === 'all' ? null : selectedState,
      category: selectedCategory === 'all' ? null : selectedCategory,
      page: 1,
      limit: filter.limit,
    };
    dispatch(setProviderFilter(newFilter));
    dispatch(fetchFilteredProviders(newFilter) as any);
    toast({ title: "Filters Applied", description: "Provider list updated.", duration: 2000 });
  };

  const resetFilters = () => {
    setSearchTerm('');
    setSelectedType('all');
    setSelectedOwnership('all');
    setSelectedCountry('Tunisia');
    setSelectedCity('all');
    setSelectedCategory('all');
    setSelectedState('all');
    const resetFilter = { search: null, type: null, ownership: null, country: 'Tunisia', city: null, state: null, category: null, page: 1, limit: 10 };
    dispatch(setProviderFilter(resetFilter));
    dispatch(fetchFilteredProviders(resetFilter)as any);
  };

  const handlePageChange = (page) => {
    dispatch(setProviderFilter({ ...filter, page }));
    dispatch(fetchFilteredProviders({ ...filter, page })as any);
  };

  const handleStateChange = (value) => {
    setSelectedState(value);
    setSelectedCity('all');
  };

  const handleEditProvider = (id) => navigate(`/admin/providers/${id}/edit`);
  const handleViewProvider = (id) => navigate(`/admin/providers/${id}`);
  const handleDeleteProvider = (id, name) => {
    if (window.confirm(`Are you sure you want to delete ${name}?`)) {
      // Add delete logic here
      toast({ title: "Provider Deleted", description: `${name} has been deleted.`, duration: 2000 });
    }
  };

  // Skeleton loading state
  const renderSkeleton = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {[...Array(6)].map((_, i) => (
        <Card key={i} className="overflow-hidden">
          <div className="h-40 bg-gray-200 animate-pulse" />
          <CardContent className="p-4 space-y-2">
            <div className="h-6 bg-gray-200 rounded w-3/4 animate-pulse" />
            <div className="flex gap-2">
              <div className="h-4 bg-gray-200 rounded w-16 animate-pulse" />
              <div className="h-4 bg-gray-200 rounded w-16 animate-pulse" />
            </div>
            <div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse" />
            <div className="h-4 bg-gray-200 rounded w-full animate-pulse" />
          </CardContent>
        </Card>
      ))}
    </div>
  );

  return (
    <div className="space-y-6 px-4 sm:px-6 lg:px-8">
      {/* Filters Section */}
      <Card className="shadow-sm">
        <CardContent className="pt-6 space-y-6">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Search Providers</h3>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
                <Input
                  type="text"
                  placeholder="Search providers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                  aria-label="Search providers"
                />
              </div>
              <Button onClick={applyFilters}>Search</Button>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Filter by Characteristics</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label htmlFor="type" className="block text-sm font-medium text-gray-700">Provider Type</label>
                <Select value={selectedType} onValueChange={setSelectedType}>
                  <SelectTrigger id="type"><SelectValue placeholder="Select Type" /></SelectTrigger>
                  <SelectContent className="bg-white z-50">
                  <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="university">University</SelectItem>
                    <SelectItem value="college">College</SelectItem>
                    <SelectItem value="high_school">High School</SelectItem>
                    <SelectItem value="elementary">Elementary School</SelectItem>
                    <SelectItem value="middle_school">Middle School</SelectItem>
                    <SelectItem value="vocational">Vocational School</SelectItem>
                    <SelectItem value="language_school">Language School</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label htmlFor="ownership" className="block text-sm font-medium text-gray-700">Ownership</label>
                <Select value={selectedOwnership} onValueChange={setSelectedOwnership}>
                  <SelectTrigger id="ownership"><SelectValue placeholder="Select Ownership" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Ownerships</SelectItem>
                    <SelectItem value="public">Public</SelectItem>
                    <SelectItem value="private">Private</SelectItem>
                    <SelectItem value="charter">Charter</SelectItem>
                    <SelectItem value="international">International</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label htmlFor="category" className="block text-sm font-medium text-gray-700">Category</label>
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger id="category"><SelectValue placeholder="Select Category" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    <SelectItem value="Liberal Arts">Liberal Arts</SelectItem>
                    <SelectItem value="Technical">Technical</SelectItem>
                    <SelectItem value="Medical">Medical</SelectItem>
                    <SelectItem value="Business">Business</SelectItem>
                    <SelectItem value="Engineering">Engineering</SelectItem>
                    <SelectItem value="Science">Science</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Filter by Location</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="state" className="block text-sm font-medium text-gray-700">State</label>
                <Select value={selectedState} onValueChange={handleStateChange}>
                  <SelectTrigger id="state"><SelectValue placeholder="Select State" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All States</SelectItem>
                    {tunisianGovernorates.map(state => (
                      <SelectItem key={state} value={state}>{state}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label htmlFor="city" className="block text-sm font-medium text-gray-700">City</label>
                <Select value={selectedCity} onValueChange={setSelectedCity} disabled={!selectedState || selectedState === 'all'}>
                  <SelectTrigger id="city"><SelectValue placeholder={selectedState === 'all' ? "Select State First" : "Select City"} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Cities</SelectItem>
                    {selectedState !== 'all' && governorateCities[selectedState]?.map(city => (
                      <SelectItem key={city} value={city}>{city}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-between gap-4">
            <Button onClick={applyFilters} className="gap-2"><Filter className="h-4 w-4" /> Apply Filters</Button>
            <Button variant="outline" onClick={resetFilters} className="gap-2"><X className="h-4 w-4" /> Reset Filters</Button>
          </div>
        </CardContent>
      </Card>

      {/* Providers List */}
      <div className="space-y-4">
        {loading ? renderSkeleton() : error ? (
          <div className="bg-red-50 p-4 rounded-md text-red-800 text-center">Error: {error}</div>
        ) : providers.length === 0 ? (
          <div className="bg-gray-50 p-8 rounded-md text-center text-gray-500">No providers found. Try adjusting your filters.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {providers.map(provider => (
              <Card key={provider.id} className="overflow-hidden hover:shadow-md transition-shadow">
                <div className="h-40 bg-gray-100 flex items-center justify-center">
                  {provider.logoUrl ? (
                    <ProviderLogo
                      filename={formatLogoUrl(provider.logoUrl)}
                      alt={`${provider.name} logo`}
                      className="max-h-full max-w-full object-contain p-4"
                    />
                  ) : (
                    <div className="text-gray-400 text-xl font-medium">No Logo</div>
                  )}
                </div>
                <CardContent className="p-4 space-y-3">
                  <h3 className="text-xl font-bold truncate">{provider.name}</h3>
                  <div className="flex flex-wrap gap-2">
                    <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">{formatProviderType(provider.type)}</span>
                    <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded capitalize">{provider.ownership}</span>
                  </div>
                  <p className="text-sm text-gray-500">{provider.city}, {provider.country}</p>
                  {provider.description && <p className="text-sm text-gray-600 line-clamp-2">{provider.description}</p>}
                  <div className="flex flex-wrap gap-3">
                    {provider.email && (
                      <a href={`mailto:${provider.email}`} className="text-blue-600 flex items-center gap-1" aria-label={`Email ${provider.name}`}>
                        <Mail className="h-4 w-4" /> <span className="sr-only">Email</span>
                      </a>
                    )}
                    {provider.website && (
                      <a href={provider.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 flex items-center gap-1" aria-label={`Visit ${provider.name} website`}>
                        <ExternalLink className="h-4 w-4" /> <span className="sr-only">Website</span>
                      </a>
                    )}
                  </div>
                  <div className="flex justify-between items-center mt-4">
                    <Button variant="outline" size="sm" onClick={() => handleViewProvider(provider.id)} aria-label={`View details of ${provider.name}`}>
                      View Details
                    </Button>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => handleEditProvider(provider.id)} aria-label={`Edit ${provider.name}`}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteProvider(provider.id, provider.name)} aria-label={`Delete ${provider.name}`}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        {!loading && !error && providers.length > 0 && paginationMeta && (
          <Pagination currentPage={paginationMeta.currentPage} totalPages={paginationMeta.totalPages} onPageChange={handlePageChange} />
        )}
      </div>
    </div>
  );
};

export default ProvidersList;